/**
 * @file: useShareReport.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { getGlobalNavigationRef } from '@euler/app/navigation/RoutesContainer';
import { kMiniprogramSharingThumbnailImageUrl } from '@euler/assets';
import { useAppLoading } from '@euler/components/loading/Provider';
import { config, envTypeToTag } from '@euler/config';
import { isDebugEnabled } from '@euler/functions/debug';
import { useIsTabletLayout } from '@euler/functions/responsiveness';
import {
  makeReportSharingLog,
  useReportSharingLogManager,
} from '@euler/functions/sharing/useReportSharingLogs';
import { useIsWeWorkAvailable } from '@euler/functions/useIsWeWorkAvailable';
import { useIsWeixinAvailable } from '@euler/functions/useIsWeixinAvailable';
import { AnalyticEvents } from '@euler/generated/AnalyticEvents';
import * as WeWork from '@euler/lib/integration/wework/WeWork';
import { WeWorkError } from '@euler/lib/integration/wework/WeWork.shared';
import {
  ReportContentType,
  ReportShareMethod,
  ShareMiniprogramParams,
} from '@euler/model';
import { reportTabKeyToReportContentType } from '@euler/model/util';
import { useServiceFactory } from '@euler/services/factory';
import { delayed, makeDebug, onErrorIgnore, showAlert } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import { isMobilePhone } from '@euler/utils/validators';
import { useActionSheet } from '@expo/react-native-action-sheet';
import { RefObject, useCallback, useEffect, useRef } from 'react';
import {
  AppState,
  Platform,
  TouchableOpacity,
  findNodeHandle,
} from 'react-native';
import Toast from 'react-native-root-toast';
import * as WeChat from 'react-native-wechat-lib';
import {
  ShareEventArgs,
  ShareReportArgs,
  reportShareTargetFromShareType,
} from './types';

const debug = makeDebug('share-report', isDebugEnabled('shareReport'));

type ShareReport = (args: ShareReportArgs) => Promise<boolean | undefined>;

type ShareReportViaSms = (
  args: ShareReportArgs & { mobile?: string },
) => Promise<void>;

export const useShareReport = () => {
  const loading = useAppLoading();
  const isTablet = useIsTabletLayout();

  const isWeixinSharingAvailable = useIsWeixinAvailable();
  const [isWeWorkSharingAvailable, isWeWorkSettingsAvailable] =
    useIsWeWorkAvailable();

  const { taskService, smsService, orderService } = useServiceFactory();
  const { showActionSheetWithOptions } = useActionSheet();
  const { appendLog, commitLog, rollbackLog } = useReportSharingLogManager();

  const appBackgroundHandler = useRef<() => void>();

  useEffect(() => {
    if (Platform.OS === 'web') return;
    const listener = AppState.addEventListener('change', state => {
      if (state === 'background' && appBackgroundHandler.current != null) {
        const handler = appBackgroundHandler.current;
        appBackgroundHandler.current = undefined;
        debug('invoke app background handler');
        handler();
      }
    });
    return () => listener.remove();
  }, []);

  const getMiniprogramSharingParams = usePersistFn(
    async (args: ShareReportArgs) => {
      const params: ShareMiniprogramParams | undefined =
        await loading.showWithAction(
          async () => {
            return await taskService.getMiniprogramReportSharingParams(
              args.target,
              {
                envTag: envTypeToTag(config.environment),
                shareType: args?.shareType,
                tab: args?.tab,
                flowId: args?.flowId,
              },
            );
          },
          {
            onError: error => {
              console.error(error);
              Toast.show('获取分享参数失败');
            },
          },
        );
      return params;
    },
  );

  const shareEventArgsFromShareArgs = useCallback(
    (args: ShareReportArgs): ShareEventArgs => ({
      orderNo: args.orderNo,
      reportNo: args.reportNo,
      licensePlateNo: args.licensePlateNo,
      taskNo: args.target.type === 'task' ? args.target.id : undefined,
      tab: args.tab,
      shareType: args.shareType,
    }),
    [],
  );

  const shareReportViaWeixin: ShareReport = useCallback(
    async (args): Promise<boolean | undefined> => {
      if (!isWeixinSharingAvailable) {
        Toast.show('微信未安装', {
          duration: 1000,
          position: Toast.positions.BOTTOM,
        });
        return;
      }

      const params = await getMiniprogramSharingParams(args);
      if (!params) return false;

      const title = `${args.licensePlateNo}的车检报告`;

      if (Platform.OS === 'web') {
        const wxopensdk = (window as any).wxopensdk;
        if (!wxopensdk) {
          Toast.show('微信分享初始化未完成,请稍后再试', {
            duration: 1000,
            position: Toast.positions.BOTTOM,
          });
          return;
        }

        let webWeixinSharingStatus: string = '';

        let cleanupWebWeixinSharing: (() => void) | undefined = undefined;

        const onBlur = () => {
          __DEV__ && console.log('window blurred, sharing... ');
          webWeixinSharingStatus = 'sharing';
        };

        const onFocus = () => {
          if (webWeixinSharingStatus !== 'sharing') {
            __DEV__ && console.log('window focused, but not sharing');
            return;
          }
          __DEV__ && console.log('window focused, sharing done');
          cleanupWebWeixinSharing?.();
          webWeixinSharingStatus = 'shared';
        };

        cleanupWebWeixinSharing = () => {
          if (webWeixinSharingStatus === 'shared') return;

          __DEV__ && console.log('log sharing result');

          const log = makeReportSharingLog({
            orderNo: args.orderNo,
            reportType: args.tab
              ? reportTabKeyToReportContentType(args.tab)
              : ReportContentType.Default,
            shareMethod: ReportShareMethod.WeixinMiniprogram,
            eventArgs: shareEventArgsFromShareArgs(args),
          });

          appendLog(log);

          __DEV__ && console.log('cleanup');

          window.removeEventListener('blur', onBlur);
          window.removeEventListener('focus', onFocus);
        };

        try {
          const ticket = await taskService.getWeixinPCOpenSDKTicket();

          window.addEventListener('blur', onBlur);
          window.addEventListener('focus', onFocus);

          wxopensdk.shareMiniProgram({
            appid: params.pcOpenAppId,
            userName: params.userName,
            txt: title,
            path: params.pagePath,
            thumburl: kMiniprogramSharingThumbnailImageUrl,
            source: 'chat', // "timeline" 朋友圈
            ticket,
          });

          setTimeout(
            () => cleanupWebWeixinSharing?.(),
            __DEV__ ? 10000 : 60000,
          );

          return;
        } catch (e) {
          console.error(e);
          Toast.show('微信分享失败,请稍后再试', {
            duration: 1000,
            position: Toast.positions.BOTTOM,
          });
          return;
        }
      }

      try {
        const log = makeReportSharingLog({
          orderNo: args.orderNo,
          reportType: args.tab
            ? reportTabKeyToReportContentType(args.tab)
            : ReportContentType.Default,
          shareMethod: ReportShareMethod.WeixinMiniprogram,
          eventArgs: shareEventArgsFromShareArgs(args),
        });

        appendLog(log);

        if (args.success != null) {
          appBackgroundHandler.current = () => args.success?.();
        }

        WeChat.shareMiniProgram({
          webpageUrl: config.linking.url,
          userName: params.userName,
          path: params.pagePath,
          title,
          hdImageUrl: kMiniprogramSharingThumbnailImageUrl,
          thumbImageUrl: kMiniprogramSharingThumbnailImageUrl,
          scene: 0,
        })
          .then(async resp => {
            debug('share result: %o', resp);
            delayed(() => {
              loading.show({
                type: 'success',
                message: '分享成功',
                duration: 2000,
              });
            }, 500);

            await commitLog(log.id);
          })
          .catch(e => {
            rollbackLog(log.id);
            console.error(e);
            Toast.show('分享失败');
          });

        return true;
      } catch (e) {
        console.error(e);
        Toast.show('分享失败');
        return false;
      }
    },
    [
      appendLog,
      commitLog,
      getMiniprogramSharingParams,
      isWeixinSharingAvailable,
      loading,
      rollbackLog,
      shareEventArgsFromShareArgs,
      taskService,
    ],
  );

  const shareReportViaWeWork: ShareReport = useCallback(
    async (args): Promise<boolean | undefined> => {
      if (!isWeWorkSharingAvailable) {
        Toast.show('企业微信未安装', {
          duration: 1000,
          position: Toast.positions.BOTTOM,
        });
        return;
      }

      if (!isWeWorkSettingsAvailable) {
        await showAlert(
          '提示',
          '对不起，当前门店尚未配置企业微信分享，请联系我们为您协助配置!',
        );
        return;
      }

      const params = await getMiniprogramSharingParams(args);
      if (!params) {
        console.error('getMiniprogramSharingParams failed');
        return false;
      }

      debug('shareReportViaWeWork params: %o', params);

      const log = makeReportSharingLog({
        orderNo: args.orderNo,
        reportType: args.tab
          ? reportTabKeyToReportContentType(args.tab)
          : ReportContentType.Default,
        shareMethod: ReportShareMethod.WecomMiniprogram,
        eventArgs: shareEventArgsFromShareArgs(args),
      });

      try {
        appendLog(log);

        if (args.success != null) {
          appBackgroundHandler.current = () => args.success?.();
        }

        debug('calling WeWork.shareMiniProgram');
        await WeWork.shareMiniProgram({
          userName: params.userName,
          pagePath: params.pagePath,
          title: `${args.shareType === 'sa' ? '* ' : ''}${
            args.licensePlateNo
          }的车检报告`,
          imageUrl: kMiniprogramSharingThumbnailImageUrl,
        })
          .then(async () => {
            delayed(() => {
              loading.show({
                type: 'success',
                message: '分享成功',
                duration: 2000,
              });
            }, 500);

            await commitLog(log.id);
          })
          .catch(e => {
            rollbackLog(log.id);
            console.error(e);
            Toast.show('分享失败');
          });

        return true;
      } catch (e) {
        rollbackLog(log.id);
        if (e instanceof WeWorkError && e.message === 'Cancelled') {
          Toast.show('已取消分享');
          debug('wework sharing canceled by user');
          return false;
        }
        console.error(e);
        Toast.show('分享失败');
        return false;
      }
    },
    [
      appendLog,
      commitLog,
      getMiniprogramSharingParams,
      isWeWorkSettingsAvailable,
      isWeWorkSharingAvailable,
      loading,
      rollbackLog,
      shareEventArgsFromShareArgs,
    ],
  );

  const shareReportViaSms: ShareReportViaSms = usePersistFn(async args => {
    const send = async (mobile: string) => {
      await smsService.shareReport({
        mobile,
        reportNo: args.reportNo,
        shareToken: args.shareToken ?? '',
        envTag: envTypeToTag(config.environment),
      });
      AnalyticEvents.default.reportSharedViaSms(
        shareEventArgsFromShareArgs(args),
      );
      loading.show({
        type: 'success',
        message: '已发送',
        duration: 2000,
      });
      await orderService
        .logReportSharing(args.orderNo, {
          reportType: args.tab
            ? reportTabKeyToReportContentType(args.tab)
            : ReportContentType.Default,
          shareMethod: ReportShareMethod.SmsLink,
          shareTarget: reportShareTargetFromShareType(args.shareType),
        })
        .catch(onErrorIgnore);
      args.success?.();
    };

    // Deferred to fire-time so this hook is callable from components
    // rendered outside a NavigationContainer (e.g. the AI overlay).
    const navigation = getGlobalNavigationRef();
    if (!navigation) {
      Toast.show('导航未就绪');
      return;
    }
    navigation.navigate('_prompt', {
      title: '发送短信报告',
      message: '输入客户手机号码然后继续',
      keyboardType: 'phone-pad',
      defaultValue: args.mobile ?? undefined,
      destructive: true,
      cancelButtonText: '取消',
      confirmButtonText: '发送',
      onChange(_value: string, notify$: any) {
        notify$.next({ type: 'error', error: undefined });
      },
      onConfirm: async (value: string, notify$: any) => {
        value = value.trim();
        if (!value) {
          throw new Error('请输入手机号码!');
        }
        if (!isMobilePhone(value)) {
          throw new Error('手机号码格式不正确!');
        }
        notify$.next({ type: 'loading' });
        try {
          await send(value);
        } catch (e) {
          throw new Error(`发送失败: ${(e as any).message}`);
        } finally {
          notify$.next({ type: 'loading', loading: false });
        }
      },
    });
  });

  const shareReportToCustomer = usePersistFn(
    async ({
      shareButtonRef,
      ...args
    }: ShareReportArgs & { shareButtonRef?: RefObject<TouchableOpacity> }) => {
      const nodeId =
        isTablet && shareButtonRef?.current
          ? findNodeHandle(shareButtonRef.current)
          : undefined;

      type OptionInfo = {
        title: string;
        disabled?: boolean;
        onPress: (params: ShareReportArgs) => Promise<void>;
      };

      const options: OptionInfo[] = [
        {
          title: '微信分享',
          disabled: !isWeixinSharingAvailable,
          onPress: async (params: ShareReportArgs) => {
            await shareReportViaWeixin(params);
          },
        },
        {
          title: '企业微信分享',
          disabled: !isWeWorkSharingAvailable,
          onPress: async (params: ShareReportArgs) => {
            await shareReportViaWeWork(params);
          },
        },
        {
          title: '短信分享',
          onPress: async (params: ShareReportArgs) => {
            await shareReportViaSms(params);
          },
        },
      ].filter(x => !x.disabled);

      if (options.length === 1) {
        await options[0].onPress(args);
        return;
      }

      const supportedShareMethods = options.map(x =>
        x.title.replace('分享', ''),
      );
      const supportedShareMethodsTitle = [
        supportedShareMethods
          .slice(0, supportedShareMethods.length - 1)
          .join('、'),
        supportedShareMethods[supportedShareMethods.length - 1],
      ].join('和');

      showActionSheetWithOptions(
        {
          title: '分享报告给车主',
          message: `选择报告分享方式，当前支持${supportedShareMethodsTitle}`,
          options: [...options.map(x => x.title), '关闭'],
          cancelButtonIndex: options.length,
          anchor: nodeId ?? undefined,
        },
        async buttonIndex => {
          if (buttonIndex != null && buttonIndex < options.length) {
            await options[buttonIndex].onPress(args);
          }
        },
      );
    },
  );

  return {
    isWeixinSharingAvailable,
    isWeWorkSharingAvailable,
    shareReportViaWeixin,
    shareReportViaWeWork,
    shareReportViaSms,
    shareReportToCustomer,
  };
};
